/**
 * Blog post detail page — "Desert Editorial" (jg) design system.
 * Posts from WISP are normalized via normalizePost() for consistent styling.
 */
import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  WISP_POSTS_API_ENDPOINT,
  getPostPublishedAt,
  formatBlogPublishedDate,
} from "../utils/wisp";
import SEO from "../components/SEO";
import { getPageSEO, generateBlogPostingSchema } from "../utils/seoData";
import { FOUNDER } from "../utils/contactInfo";
import {
  shouldNoindexBlogSlug,
  rewriteApexAgencyUrls,
  getBlogRedirect,
} from "../utils/blogIndexPolicy";
import { resolveBlogImage } from "../utils/blogEnhance";
import { getLocalBlogPost } from "../utils/mergeBlogPosts";
import JgByline from "./jg/JgByline";
import { cleanWispPostHtml } from "../utils/wispPostCleanup";
import JgShell from "./jg/JgShell";
import JgCta from "./jg/JgCta";
import NotFound from "../pages/NotFound";

const POST_NOT_FOUND = "Blog post not found";

const getReadingTime = (html) => {
  if (!html) return 1;
  const text = html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
};

const slugify = (text) => {
  return String(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
};

// Normalize post so new blogs from WISP always have consistent styling
const normalizePost = (p) => ({
  ...p,
  title: p?.title || "Untitled",
  description: p?.description || p?.excerpt || "",
  image: resolveBlogImage(p?.image || p?.featuredImage || p?.coverImage, p?.slug),
  content: rewriteApexAgencyUrls(
    p?.source === "local"
      ? p?.content || ""
      : cleanWispPostHtml(p?.content || "", p?.slug)
  ),
  tags: p?.tags || [],
  publishedAt: getPostPublishedAt(p),
});

/**
 * Build-time prerender embeds the post JSON so Googlebot's renderer never
 * sees robots noindex on a loading shell (a common "crawled, not indexed" cause).
 */
function readPrerenderedPost(slug) {
  if (typeof document === "undefined" || !slug) return null;
  const el = document.getElementById("jg-prerender-post");
  if (!el?.textContent) return null;
  try {
    const data = JSON.parse(el.textContent);
    const raw = data?.post || data;
    const postSlug = String(raw?.slug || data?.slug || "").trim();
    if (!postSlug || postSlug !== slug) return null;
    if (!raw?.title && !raw?.content) return null;
    return normalizePost({ ...raw, slug: postSlug });
  } catch {
    return null;
  }
}

const asideHeadingStyle = {
  marginBottom: "1rem",
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(() => {
    const local = getLocalBlogPost(slug);
    return readPrerenderedPost(slug) || (local ? normalizePost(local) : null);
  });
  const [loading, setLoading] = useState(() => {
    const local = getLocalBlogPost(slug);
    return !(readPrerenderedPost(slug) || local);
  });
  const [error, setError] = useState(null);
  const [toc, setToc] = useState([]);
  const contentRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const local = getLocalBlogPost(slug);
    const bootstrapped =
      readPrerenderedPost(slug) || (local ? normalizePost(local) : null);
    setPost(bootstrapped);
    setError(null);
    setLoading(!bootstrapped);
    // Retired slugs redirect on render; don't let a late fetch land on the target.
    if (getBlogRedirect(slug)) return undefined;
    let cancelled = false;

    const fetchSingleBlogPost = async () => {
      if (!slug) {
        setError("No blog post slug provided in the URL.");
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(`${WISP_POSTS_API_ENDPOINT}/${slug}`);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch post: ${response.status} ${response.statusText}`
          );
        }
        const data = await response.json();
        if (cancelled) return;
        if (data.post) {
          setPost(normalizePost({ ...data.post, slug }));
          setError(null);
        } else if (!bootstrapped) {
          throw new Error(POST_NOT_FOUND);
        }
      } catch (err) {
        if (!cancelled && !bootstrapped) {
          console.error("Error fetching blog post:", err);
          setPost(null);
          setError(err.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSingleBlogPost();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Build the sticky TOC from headings
  useEffect(() => {
    if (!post?.content || !contentRef.current) return;

    const headings = contentRef.current.querySelectorAll("h2, h3, h4");
    const items = [];
    const usedIds = new Set();
    headings.forEach((el, i) => {
      let id = el.id || slugify(el.textContent) || `heading-${i}`;
      if (usedIds.has(id)) {
        id = `${id}-${i}`;
      }
      usedIds.add(id);
      el.id = id;
      items.push({
        id,
        text: el.textContent?.trim() || "",
        level: parseInt(el.tagName.charAt(1), 10),
      });
    });
    setToc(items);
  }, [post?.content]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareTitle = post?.title || "";

  const seoData = getPageSEO("blog");
  const suppressFromIndex = shouldNoindexBlogSlug(slug);
  const hasRenderablePost = Boolean(post);
  const isMissingPost = !loading && !post;
  const articleTitle = post?.title || seoData.title;
  const articleDescription = post?.description || seoData.description;
  const articleKeywords = Array.isArray(post?.tags)
    ? post.tags
        .map((tag) => (typeof tag === "string" ? tag : tag?.name))
        .filter(Boolean)
        .join(", ")
    : seoData.keywords;
  const articleSchema = generateBlogPostingSchema(post, slug);
  const seoTitle = hasRenderablePost
    ? articleTitle
    : error
      ? "Post not found | Jamil Global"
      : seoData.title;
  const seoDescription = hasRenderablePost
    ? articleDescription
    : error
      ? "This post does not exist or could not be loaded."
      : seoData.description;
  const publishedDisplay = formatBlogPublishedDate(post?.publishedAt);
  const primaryTag = post?.tags?.length
    ? typeof post.tags[0] === "string"
      ? post.tags[0]
      : post.tags[0]?.name
    : null;

  const handleShare = (platform) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(shareTitle);

    const urls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      copy: shareUrl,
    };

    if (platform === "copy") {
      navigator.clipboard?.writeText(shareUrl);
      return;
    }
    if (urls[platform])
      window.open(urls[platform], "_blank", "noopener,noreferrer");
  };

  // Retired slug reached by in-app navigation (the edge 308s direct hits).
  const retiredTarget = getBlogRedirect(slug);
  if (retiredTarget) {
    return <Navigate to={retiredTarget} replace />;
  }

  // Unknown slug: the edge already answered HTTP 404; show the shared page.
  if (isMissingPost && error === POST_NOT_FOUND) {
    return <NotFound />;
  }

  return (
    <JgShell>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={hasRenderablePost ? articleKeywords : seoData.keywords}
        url={`/blog/${slug}`}
        type={hasRenderablePost ? "article" : "website"}
        author={FOUNDER.name}
        structuredData={hasRenderablePost ? articleSchema : null}
        publishedTime={
          hasRenderablePost ? post?.publishedAt || post?.createdAt : null
        }
        modifiedTime={
          hasRenderablePost
            ? post?.updatedAt || post?.publishedAt || post?.createdAt
            : null
        }
        noindex={isMissingPost || suppressFromIndex}
      />

      <section
        className="jg-section-pad"
        aria-label="Article"
        style={{ paddingTop: "calc(64px + clamp(3rem, 8vh, 5.5rem))" }}
      >
        <div className="jg-wrap">
          {loading && !post && (
            <p className="jg-label jg-label--bare" role="status">
              Loading article…
            </p>
          )}

          {error && (
            <div>
              <p
                className="jg-label jg-label--bare"
                style={{ color: "var(--jg-oxide-deep)", marginBottom: "2rem" }}
                role="alert"
              >
                {error}
              </p>
              <Link to="/blog" className="jg-btn jg-btn--solid">
                ← Back to blog
              </Link>
            </div>
          )}

          {post && !error && (
            <>
              <p className="jg-label" style={{ marginBottom: "1.5rem" }}>
                <Link to="/blog">Journal</Link>
                {publishedDisplay && post.publishedAt && (
                  <>
                    {" · "}
                    <time dateTime={post.publishedAt}>{publishedDisplay}</time>
                  </>
                )}
                {primaryTag && <>{" · "}{primaryTag}</>}
                <span className="jg-read-time">
                  {" · "}
                  {getReadingTime(post.content)} min read
                </span>
              </p>

              <h1
                style={{
                  fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)",
                  lineHeight: 1.02,
                  letterSpacing: "-0.02em",
                  fontWeight: 430,
                  maxWidth: "18em",
                }}
              >
                {post.title}
              </h1>

              <JgByline />

              {post.image ? (
                <figure className="jg-article-hero">
                  <img src={post.image} alt="" />
                </figure>
              ) : null}

              <div
                style={{
                  marginTop: "clamp(2rem, 5vh, 3.5rem)",
                  borderTop: "1px solid var(--jg-line-strong)",
                  paddingTop: "clamp(2rem, 5vh, 3.5rem)",
                }}
              >
                <div className="jg-two-col">
                  <aside className="jg-sticky-aside">
                    {toc.length > 0 && (
                      <nav aria-label="On this page">
                        <p className="jg-label" style={asideHeadingStyle}>
                          On this page
                        </p>
                        <ul
                          style={{
                            listStyle: "none",
                            margin: 0,
                            padding: 0,
                            display: "grid",
                            gap: "0.55rem",
                          }}
                        >
                          {toc.map((item) => (
                            <li
                              key={item.id}
                              style={{
                                paddingLeft:
                                  item.level === 3
                                    ? "1rem"
                                    : item.level === 4
                                      ? "1.5rem"
                                      : 0,
                              }}
                            >
                              <a
                                href={`#${item.id}`}
                                style={{
                                  fontSize: "0.875rem",
                                  color: "var(--jg-ink-soft)",
                                }}
                              >
                                {item.text.length > 60
                                  ? item.text.slice(0, 60) + "..."
                                  : item.text}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </nav>
                    )}

                    <div style={{ marginTop: toc.length ? "2.5rem" : 0 }}>
                      <p className="jg-label" style={asideHeadingStyle}>
                        Share this article
                      </p>
                      <div className="jg-tags">
                        <button
                          type="button"
                          className="jg-tag"
                          style={{ cursor: "pointer", background: "transparent" }}
                          onClick={() => handleShare("twitter")}
                        >
                          Twitter / X
                        </button>
                        <button
                          type="button"
                          className="jg-tag"
                          style={{ cursor: "pointer", background: "transparent" }}
                          onClick={() => handleShare("linkedin")}
                        >
                          LinkedIn
                        </button>
                        <button
                          type="button"
                          className="jg-tag"
                          style={{ cursor: "pointer", background: "transparent" }}
                          onClick={() => handleShare("copy")}
                        >
                          Copy link
                        </button>
                      </div>
                    </div>
                  </aside>

                  <div>
                    <article
                      ref={contentRef}
                      className="jg-prose"
                      dangerouslySetInnerHTML={{ __html: post.content }}
                    />

                    {post.tags && post.tags.length > 0 && (
                      <div
                        className="jg-tags"
                        style={{
                          marginTop: "2.5rem",
                          paddingTop: "1.5rem",
                          borderTop: "1px solid var(--jg-line)",
                        }}
                      >
                        {post.tags.map((tag, i) => (
                          <span key={tag?.id || i} className="jg-tag">
                            #{typeof tag === "string" ? tag : tag?.name}
                          </span>
                        ))}
                      </div>
                    )}

                    <div style={{ marginTop: "2.5rem" }}>
                      <Link to="/blog" className="jg-link">
                        ← Back to blog
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      <JgCta />
    </JgShell>
  );
};

export default BlogPost;
